import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import type { HomeData, RecentCase, JokeData, AllTasksData, CalendarData } from '../types/home';
import type { ApiResponse } from '../types/api';
import { useAuth } from './useAuth';

/* ──────────────────────────────────────────────────
   useHomeTasks — fetches tasks + caseDetailsMap
   ────────────────────────────────────────────────── */
export function useHomeTasks() {
  return useQuery<HomeData>({
    queryKey: ['home', 'tasks'],
    queryFn: async () => {
      const { data } = await api.get<HomeData>('/api/home');
      return data;
    },
    staleTime: 60_000,
  });
}

/* ──────────────────────────────────────────────────
   useRecentCases — reads localStorage, fetches details
   ────────────────────────────────────────────────── */
function getRecentCaseRefs(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem('recentCases') ?? '[]') as unknown[];
    return raw
      .map((c) => {
        if (typeof c === 'string') return c;
        if (c && typeof c === 'object')
          return (
            (c as Record<string, string>).caseRef ?? (c as Record<string, string>).CaseRef ?? ''
          );
        return '';
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

export function useRecentCases() {
  const refs = getRecentCaseRefs();

  return useQuery<RecentCase[]>({
    queryKey: ['home', 'recentCases', refs],
    queryFn: async () => {
      if (!refs.length) return [];
      const { data } = await api.post<{ cases: RecentCase[] }>('/api/home/cases', {
        caseRefs: refs,
      });
      const cases = data.cases ?? [];

      // Sort to match localStorage order (newest first)
      const caseMap = new Map<string, RecentCase>();
      cases.forEach((c) => {
        const ref = c.CaseRef ?? c.caseRef ?? '';
        if (ref) caseMap.set(ref, c);
      });
      return refs.map((ref) => caseMap.get(ref)).filter(Boolean) as RecentCase[];
    },
    staleTime: 60_000,
    enabled: refs.length > 0,
  });
}

/* ──────────────────────────────────────────────────
   useUpdateTask — mutation to update a task
   ────────────────────────────────────────────────── */
interface UpdateTaskPayload {
  taskID: string;
  desc: string;
  deadline: string;
  status: string;
  category?: string;
}

export function useUpdateTask() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (payload: UpdateTaskPayload) => {
      await api.post('/api/home/task/update', {
        username: user?.username,
        ...payload,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['home', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['allTasks'] });
    },
  });
}

/* ──────────────────────────────────────────────────
   useDeleteTask — mutation to delete a task
   ────────────────────────────────────────────────── */
export function useDeleteTask() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (taskID: string) => {
      await api.post('/api/home/task/delete', {
        username: user?.username,
        taskID,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['home', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['allTasks'] });
    },
  });
}

/* ──────────────────────────────────────────────────
   useDailyJoke — fetch the daily joke
   ────────────────────────────────────────────────── */
export function useDailyJoke(enabled: boolean) {
  return useQuery<JokeData>({
    queryKey: ['dailyJoke'],
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<JokeData>>('/api/dad-joke');
      if (!data.success || !data.data) throw new Error(data.message ?? 'Failed to fetch joke');
      return data.data;
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

/* ──────────────────────────────────────────────────
   useAllTasks — fetches paginated + filtered tasks (tasks page)
   ────────────────────────────────────────────────── */
interface AllTasksParams {
  page?: number;
  limit?: number;
  category?: string;
  status?: string;
  assignedTo?: string;
  dateFrom?: string;
  dateTo?: string;
  caseTypeId?: string | number;
}

export function useAllTasks(params: AllTasksParams = {}) {
  return useQuery<AllTasksData>({
    queryKey: ['allTasks', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.limit) searchParams.set('limit', String(params.limit));
      if (params.category) searchParams.set('category', params.category);
      if (params.status) searchParams.set('status', params.status);
      if (params.assignedTo) searchParams.set('assignedTo', params.assignedTo);
      if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
      if (params.dateTo) searchParams.set('dateTo', params.dateTo);
      if (
        params.caseTypeId !== undefined &&
        params.caseTypeId !== '' &&
        params.caseTypeId !== null
      ) {
        searchParams.set('caseTypeId', String(params.caseTypeId));
      }
      const { data } = await api.get<AllTasksData>(`/api/home/tasks/all?${searchParams}`);
      return data;
    },
    staleTime: 60_000,
  });
}

/* ──────────────────────────────────────────────────
   useCreateTask — mutation to create a task from /tasks page
   ────────────────────────────────────────────────── */
interface CreateTaskPayload {
  desc: string;
  deadline: string;
  assigned_to?: string;
  category?: string;
  caseRef?: string;
  allow_reminders?: boolean;
  reminder_datetime?: string;
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateTaskPayload) => {
      const { data } = await api.post('/api/home', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['home', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['allTasks'] });
      qc.invalidateQueries({ queryKey: ['taskCalendar'] });
    },
  });
}

/* ──────────────────────────────────────────────────
   useReassignTask — mutation to reassign a task
   ────────────────────────────────────────────────── */
export function useReassignTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { taskId: string; newUserId: string }) => {
      await api.post('/api/home/task/reassign', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['home', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['allTasks'] });
    },
  });
}

/* ──────────────────────────────────────────────────
   useBulkReassignTasks — mutation to reassign multiple tasks
   ────────────────────────────────────────────────── */
export function useBulkReassignTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { taskIds: string[]; newUserId: string }) => {
      const { data } = await api.post('/api/home/tasks/bulk-reassign', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['home', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['allTasks'] });
    },
  });
}

/* ──────────────────────────────────────────────────
   useAdvancedReassign — filter-based bulk reassign with optional dry-run
   ────────────────────────────────────────────────── */
export interface AdvancedReassignFilters {
  caseTypeId?: number | null;
  stageId?: number | null;
  category?: string | null;
  statuses?: string[];
  deadlineFrom?: string | null;
  deadlineTo?: string | null;
}

export interface AdvancedReassignPayload {
  fromUserId: string;
  toUserIds: string[];
  filters: AdvancedReassignFilters;
  dryRun: boolean;
}

export interface AdvancedReassignResult {
  count: number;
  sample: Array<{
    TaskID: string;
    CaseRef: string | null;
    Description: string | null;
    Status: string | null;
    Deadline: string | null;
    Category: string | null;
  }>;
  reassigned: number;
  dryRun: boolean;
  distribution: Record<string, number>;
}

export function useAdvancedReassign() {
  const qc = useQueryClient();
  return useMutation<AdvancedReassignResult, Error, AdvancedReassignPayload>({
    mutationFn: async (payload) => {
      const { data } = await api.post('/api/home/tasks/advanced-reassign', payload);
      return data;
    },
    onSuccess: (data) => {
      if (!data.dryRun && data.reassigned > 0) {
        qc.invalidateQueries({ queryKey: ['home', 'tasks'] });
        qc.invalidateQueries({ queryKey: ['allTasks'] });
        qc.invalidateQueries({ queryKey: ['caseTasks'] });
        qc.invalidateQueries({ queryKey: ['taskCalendar'] });
      }
    },
  });
}

/* ──────────────────────────────────────────────────
   useTaskCalendar — fetch task counts by date
   ────────────────────────────────────────────────── */
export function useTaskCalendar(start: string, end: string) {
  return useQuery<CalendarData>({
    queryKey: ['taskCalendar', start, end],
    queryFn: async () => {
      const { data } = await api.get<{ counts: CalendarData }>(
        `/api/home/tasks/calendar?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
      );
      return data.counts;
    },
    staleTime: 5 * 60_000,
    enabled: !!start && !!end,
  });
}
